import React, { useRef, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { compose } from "react-apollo";
import statusRunning from "assets/img/status-running.svg";
import AlertModal from "components/AlertModal/AlertModal";
import { withAuthentication } from "utils/withAuthentication";
import withAuthorizedOperator from "utils/withAuthorizedOperator";

const colors = {
  running: "#007bff",
  paused: "#ffcc00",
  aborted: "#ff3b30",
  completed: "#28a745",
  waiting: "#888",
  waiting_refill: "#ff9500",
  arrow: "#000"
};

const StatusIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ status }) => colors[status]};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  transition: filter 0.2s;
  color: white;
  
  &:hover {
    filter: brightness(0.9);
  }
`;

const PauseIcon = () => (
  <div style={{ display: "flex", gap: "4px" }}>
    <div style={{ width: "5px", height: "20px", background: "currentColor", borderRadius: "2px" }} />
    <div style={{ width: "5px", height: "20px", background: "currentColor", borderRadius: "2px" }} />
  </div>
);

const ResumeIcon = ({ color = "white", disabled }) => (
  <div style={{
    width: 0,
    height: 0,
    borderTop: "10px solid transparent",
    borderBottom: "10px solid transparent",
    borderLeft: `16px solid ${disabled ? "#bbb" : color}`,
    borderRadius: "3px",
    marginLeft: "2px"
  }} />
);

const XIcon = ({ color = "white", disabled }) => {
  const iconColor = disabled ? "#bbb" : color;
  return (
    <div style={{ position: "relative", width: "18px", height: "18px" }}>
      <div style={{
        position: "absolute",
        top: "8px",
        left: 0,
        width: "18px",
        height: "2px",
        background: iconColor,
        transform: "rotate(45deg)",
        borderRadius: "2px"
      }} />
      <div style={{
        position: "absolute",
        top: "8px",
        left: 0,
        width: "18px",
        height: "2px",
        background: iconColor,
        transform: "rotate(-45deg)",
        borderRadius: "2px"
      }} />
    </div>
  );
};

const CheckIcon = () => (
  <div style={{
    width: "20px",
    height: "12px",
    borderLeft: "3px solid white",
    borderBottom: "3px solid white",
    transform: "rotate(-45deg)",
    borderRadius: "2px",
    marginTop: "2px"
  }} />
);

const ClockIcon = () => (
  <div style={{
    position: "relative",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    border: "2px solid white"
  }}>
    <div style={{
      position: "absolute",
      top: "4px",
      left: "9px",
      width: "2px",
      height: "7px",
      background: "white",
      borderRadius: "1px"
    }} />
    <div style={{
      position: "absolute",
      top: "9px",
      left: "9px",
      width: "5px",
      height: "2px",
      background: "white",
      borderRadius: "1px"
    }} />
  </div>
);

const ActionButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 4px;
  background: transparent;
  cursor: pointer;
  transition: background 0.2s, filter 0.2s, border-color 0.2s, color 0.2s;

  ${({ type }) => type === "pause" && css`
    border: 2px solid ${colors.paused};
    color: ${colors.paused};
    &:hover:not(:disabled) {
      background: rgba(255, 204, 0, 0.1);
    }
  `}

  ${({ type }) => type === "resume" && css`
    border: 2px solid ${colors.running};
    color: ${colors.running};
    &:hover:not(:disabled) {
      background: rgba(0, 123, 255, 0.1);
    }
  `}

  ${({ type }) => type === "abort" && css`
    border: 2px solid ${colors.aborted};
    color: ${colors.aborted};
    &:hover:not(:disabled) {
      background: rgba(255, 59, 48, 0.1);
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    filter: grayscale(0.3);
    border-color: #bbb !important;
    color: #bbb !important;
    background: transparent;
  }
`;

const Arrow = styled.div`
  flex: 1;
  max-width: 250px;
  min-width: 50px;
  height: 2px;
  background: ${colors.arrow};
  margin: 0 15px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    right: -6px;
    top: -5px;
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-left: 8px solid ${colors.arrow};
  }
`;

const StepWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  margin-top: 10px;
`;

const StepBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100px;
`;

const StepInfo = styled.div`
  text-align: center;
  margin-top: 10px;
  font-size: 0.85rem;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 8px;
`;

const ParentBox = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 200px;
  max-width: 300px;
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: hidden;
`;

const TopBox = styled.div`
  height: 52px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
`;

const BottomBox = styled.div`
  min-height: 130px;
  max-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start; 
  align-items: center;
  padding: 8px;
  gap: 4px;
  background-color: #ffffff;
`;

const Icon = styled.img`
  width: 45%;
  height: 45%;
`;

function getStepStatus(step) {
  if (step.abort_time) return "aborted";
  if (step.end_time) return "completed";
  // Hold por abastecimento do silo: o controller marca status "waiting_refill" no step
  // enquanto segura a dosagem. Vem antes de start_time porque o hold acontece antes de a
  // dosagem comecar (o step ainda nao tem start_time).
  if (step.status === "waiting_refill" && !step.start_time) return "waiting_refill";
  if (step.start_time) return step.paused ? "paused" : "running";
  return "waiting";
}

function StatusIconContent({ status }) {
  switch (status) {
    case "running": return <Icon src={statusRunning} />;
    case "paused": return <PauseIcon />;
    case "aborted": return <XIcon />;
    case "completed": return <CheckIcon />;
    case "waiting": return <ClockIcon />;
    case "waiting_refill": return <ClockIcon />;
    default: return null;
  }
}

const StepsContainer = styled.div`
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  padding-bottom: 10px;
  scroll-behavior: smooth;
  scrollbar-width: thin;
  scrollbar-color: #ccc transparent;
`;

const StepFlow = ({
  steps,
  onPauseResume,
  onAbort,
  currentUser,
  authorizedOperator,
}) => {
  const stepsArray = Object.entries(steps).map(([id, s]) => ({ id, ...s }));
  const containerRef = useRef(null);
  const stepRefs = useRef([]);
  // const restrictedRoles = new Set(["API_USER", "RESTRICTED_USER"]);
  const restrictedRoles = new Set(["API_USER"]);
  const [disableActions, setDisableActions] = useState(true);

  useEffect(() => {
    if (!stepsArray.length) return;

    const runningOrPaused = stepsArray.find(
      (s) => ["running", "paused"].includes(getStepStatus(s))
    );
    const finishedOrAborted = [...stepsArray]
      .reverse()
      .find((s) => ["completed", "aborted"].includes(getStepStatus(s)));
    const waiting = stepsArray.find((s) => getStepStatus(s) === "waiting");

    const targetStep =
      runningOrPaused || finishedOrAborted || waiting || stepsArray[0];

    const targetIndex = stepsArray.findIndex((s) => s.id === targetStep.id);
    const targetElement = stepRefs.current[targetIndex];
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [stepsArray]);

  const handleAction = (
    action,
    step,
    modalTitle,
    modalText
  ) => {
    AlertModal.fire({
      title: modalTitle,
      html: modalText,
      type: "warning",
      showCancelButton: true,
      confirmButtonColor: "#1D84C6",
      confirmButtonText: "Confirmar",
      cancelButtonText: "Voltar",
      reverseButtons: true,
    })
      .then(result => {
        if (result.value) {
          action(step);
        }
      });
  };

  useEffect(() => {
    if (
      currentUser
      && !restrictedRoles.has(currentUser.role)
      && authorizedOperator
      && authorizedOperator.id === currentUser.id
    ) {
      setDisableActions(false);
    } else {
      setDisableActions(true);
    }
  }, [currentUser, authorizedOperator]);

  return (
    <StepsContainer ref={containerRef}>
      {stepsArray.map((step, index) => {
        const status = getStepStatus(step);
        const availableStatus = new Set(["running", "paused", "waiting"]);
        const showActions = availableStatus.has(status);

        return (
          <StepWrapper
            key={step.id}
            ref={(el) => { stepRefs.current[index] = el; }}
            style={{
              marginLeft: index === 0 ? "20px" : 0,
              marginRight: index === stepsArray.length - 1 ? "20px" : 0,
            }}
          >
            <StepBlock>
              <ParentBox>
                <TopBox>
                  <StatusIcon status={status}>
                    <StatusIconContent status={status} />
                  </StatusIcon>
                </TopBox>

                <BottomBox>
                  <StepInfo>
                    <div><strong>{step.name}</strong></div>
                    {status === "waiting_refill" && (
                      <div
                        style={{
                          fontSize: "12px",
                          whiteSpace: "normal",
                          color: step.hold_alert ? colors.aborted : colors.waiting_refill,
                          fontWeight: step.hold_alert ? "bold" : "normal",
                        }}
                      >
                        Aguardando abastecimento
                        {typeof step.waiting_seconds === "number" ? ` (${step.waiting_seconds}s)` : ""}
                      </div>
                    )}
                    {/* <div>{status}</div> */}
                  </StepInfo>

                  {showActions && (
                    <ActionsRow>
                      {status === "running" && (
                        <ActionButton
                          type="pause"
                          onClick={() => (
                            handleAction(
                              onPauseResume,
                              step,
                              "Pausar Processo",
                              "Você deseja pausar este processo?"
                            )
                          )}
                          disabled={disableActions}
                        >
                          <PauseIcon />
                        </ActionButton>
                      )}
                      {status === "paused" && (
                        <ActionButton
                          type="resume"
                          onClick={() => (
                            handleAction(
                              onPauseResume,
                              step,
                              "Continuar Processo",
                              "Você deseja dar seguimento no processo?"
                            )
                          )}
                          disabled={disableActions}
                        >
                          <ResumeIcon color={colors.running} disabled={disableActions} />
                        </ActionButton>
                      )}
                      <ActionButton
                        type="abort"
                        onClick={() => (
                          handleAction(
                            onAbort,
                            step,
                            "Abortar Processo",
                            "Você deseja abortar este processo processo?"
                          )
                        )}
                        disabled={disableActions}
                      >
                        <XIcon color={colors.aborted} disabled={disableActions} />
                      </ActionButton>
                    </ActionsRow>
                  )}
                </BottomBox>
              </ParentBox>
            </StepBlock>

            {index !== stepsArray.length - 1 && <Arrow />}
          </StepWrapper>
        );
      })}
    </StepsContainer>
  );
};

export default compose(
  withAuthorizedOperator,
  withAuthentication
)(StepFlow);
